const GA_ID = String(import.meta.env.VITE_GA_ID || "").trim();

export const ANALYTICS_CONSENT_COOKIE = "storizone_analytics_consent";
export const ANALYTICS_CONSENT_KEY = "storizone:analytics-consent";
const ATTRIBUTION_KEY = "storizone:campaign-attribution";
const TRACKED_ONCE_PREFIX = "storizone:analytics-once:";

const ATTRIBUTION_PARAMETERS = [
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "_gl",
  "plan",
  "billing",
  "offer",
  "theme",
  "locale",
  "ga_debug",
] as const;

export type AnalyticsConsentValue = "granted" | "denied" | "unknown";
export type AcquisitionEventParameters = Record<
  string,
  string | number | boolean | undefined
>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function readConsentCookie(): AnalyticsConsentValue {
  if (typeof document === "undefined") return "unknown";
  const prefix = `${ANALYTICS_CONSENT_COOKIE}=`;
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
  return value === "granted" || value === "denied" ? value : "unknown";
}

function consentCookieDomain(): string {
  if (typeof window === "undefined") return "";
  const hostname = String(window.location?.hostname || "").toLowerCase();
  return hostname === "storizone.com" || hostname.endsWith(".storizone.com")
    ? "; Domain=.storizone.com"
    : "";
}

export function isAcquisitionAnalyticsConfigured(): boolean {
  return Boolean(GA_ID);
}

export function getAnalyticsConsent(): AnalyticsConsentValue {
  if (typeof window === "undefined") return "unknown";
  const cookieValue = readConsentCookie();
  if (cookieValue !== "unknown") return cookieValue;

  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return stored === "granted" || stored === "denied" ? stored : "unknown";
  } catch {
    return "unknown";
  }
}

export function setAnalyticsConsent(
  value: Exclude<AnalyticsConsentValue, "unknown">,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
  } catch {
    // The shared first-party cookie remains the source of truth.
  }
  // A synchronous cookie write is required so the cross-host consent choice is
  // available immediately after navigation.
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${ANALYTICS_CONSENT_COOKIE}=${value}; Path=/; Max-Age=31536000; SameSite=Lax; Secure${consentCookieDomain()}`;
}

export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === "granted";
}

function attributionFromSearch(search: string): Record<string, string> {
  const values: Record<string, string> = {};
  const params = new URLSearchParams(search);
  for (const key of ATTRIBUTION_PARAMETERS) {
    const value = params.get(key)?.trim();
    if (value) values[key] = value.slice(0, 500);
  }
  return values;
}

export function persistCampaignAttribution(): Record<string, string> {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return {};
  const current = attributionFromSearch(window.location.search);

  try {
    const previous = JSON.parse(
      window.sessionStorage.getItem(ATTRIBUTION_KEY) || "{}",
    ) as Record<string, string>;
    const merged = { ...previous, ...current };
    if (Object.keys(current).length > 0) {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(merged));
    }
    return merged;
  } catch {
    return current;
  }
}

export function getCampaignAttribution(): Record<string, string> {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return {};
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(ATTRIBUTION_KEY) || "{}",
    ) as Record<string, string>;
    return { ...stored, ...attributionFromSearch(window.location.search) };
  } catch {
    return attributionFromSearch(window.location.search);
  }
}

function isDebugMode(): boolean {
  return getCampaignAttribution().ga_debug === "1";
}

export function initializeAcquisitionAnalytics(): void {
  if (
    typeof window === "undefined" ||
    !GA_ID ||
    !hasAnalyticsConsent() ||
    window.gtag
  ) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("set", "linker", {
    domains: ["storizone.com", "admin.storizone.com"],
    accept_incoming: true,
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, {
    send_page_view: false,
    cookie_domain: "auto",
    ...(isDebugMode() ? { debug_mode: true } : {}),
  });

  if (!document.querySelector(`script[data-storizone-ga="${GA_ID}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    script.dataset.storizoneGa = GA_ID;
    document.head.append(script);
  }
}

export function trackAcquisitionEvent(
  eventName: string,
  parameters: AcquisitionEventParameters = {},
): void {
  if (!GA_ID || !hasAnalyticsConsent()) return;
  persistCampaignAttribution();
  initializeAcquisitionAnalytics();
  window.gtag?.("event", eventName, {
    transport_type: "beacon",
    ...(isDebugMode() ? { debug_mode: true } : {}),
    ...parameters,
  });
}

export function trackAcquisitionEventOnce(
  eventName: string,
  parameters: AcquisitionEventParameters = {},
): void {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  const key = `${TRACKED_ONCE_PREFIX}${eventName}`;
  try {
    if (window.sessionStorage.getItem(key) === "1") return;
    window.sessionStorage.setItem(key, "1");
  } catch {
    // If storage is unavailable, sending is preferable to losing the event.
  }
  trackAcquisitionEvent(eventName, parameters);
}

export function trackAdminPageView(): void {
  if (typeof window === "undefined") return;
  trackAcquisitionEvent("page_view", {
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title: document.title,
    app: "admin",
  });
}

export function setAnalyticsUserId(userId?: string | null): void {
  if (!userId || !GA_ID || !hasAnalyticsConsent()) return;
  initializeAcquisitionAnalytics();
  window.gtag?.("config", GA_ID, { user_id: userId, send_page_view: false });
}
